// Copyright © 2026 Qcells. All rights reserved.
//
// This software is proprietary and confidential. Unauthorized use,
// duplication, or distribution of software is strictly prohibited.

/**
 * Application Constants
 * Centralized configuration for API endpoints, colors, and other app-wide constants
 */

// API Configuration
export const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000';
export const API_PREFIX = '/api';

// HITL (Human-in-the-Loop) plan approvals.
export const HITL_APPROVAL_TYPE = 'PlanApproval_v1';

// Full API URL helper
export const getApiUrl = (endpoint: string): string => {
  const cleanEndpoint = endpoint.startsWith('/') ? endpoint : `/${endpoint}`;
  return `${API_BASE_URL}${cleanEndpoint}`;
};

// API Endpoints
// Only an explicit null is the fleet: an empty or malformed id goes to the site
// route and fails there (the BFF's UUID pattern answers 422) rather than
// quietly widening to every site.
const workOrdersBase = (siteId: string | null) =>
  siteId === null ? '/api/v1/work-orders' : `/api/v1/sites/${siteId}/work-orders`;

export const ENDPOINTS = {
  // Me endpoints (current user resolved from JWT by downstream service)
  ME: '/api/v1/me',

  // User endpoints
  USERS: '/api/v1/users',
  INVITE_USER: '/api/v1/users/invite',
  USER_BY_ID: (userId: string) => `/api/v1/users/${userId}`,

  // Digital Twin endpoints
  DIGITAL_TWIN_DETAILS: (siteId: string) => `/api/v1/digital-twin/datacenters/details/${siteId}`,
  DIGITAL_TWIN_MODEL: '/api/v1/digital-twin/model',

  // AI Onboarding workflow
  ONBOARDING_RUN: '/api/v1/ai/onboarding/run',
  ONBOARDING_RESUME: '/api/v1/ai/onboarding/resume',

  // Onboarding coverage (BFF proxy for the coverage service; paths carry
  // both site and session ids because the downstream repository is keyed
  // by the pair)
  COVERAGE_ACTIVE_SESSION: (siteId: string) => `/api/v1/coverage/sites/${siteId}/sessions/active`,
  COVERAGE_SESSIONS: (siteId: string) => `/api/v1/coverage/sites/${siteId}/sessions`,
  COVERAGE_SESSION: (siteId: string, sessionId: string) =>
    `/api/v1/coverage/sites/${siteId}/sessions/${sessionId}`,
  COVERAGE_SITE_PROFILE: (siteId: string, sessionId: string) =>
    `/api/v1/coverage/sites/${siteId}/sessions/${sessionId}/site-profile`,
  COVERAGE_CHECKLIST: (siteId: string, sessionId: string) =>
    `/api/v1/coverage/sites/${siteId}/sessions/${sessionId}/checklist`,
  COVERAGE_BYPASS: (siteId: string, sessionId: string, itemId: string) =>
    `/api/v1/coverage/sites/${siteId}/sessions/${sessionId}/checklist/${itemId}/bypass`,
  COVERAGE_SUBMIT: (siteId: string, sessionId: string) =>
    `/api/v1/coverage/sites/${siteId}/sessions/${sessionId}/submit`,
  COVERAGE_STAGING: (siteId: string, sessionId: string) =>
    `/api/v1/coverage/sites/${siteId}/sessions/${sessionId}/staging`,
  COVERAGE_APPROVAL: (siteId: string, sessionId: string) =>
    `/api/v1/coverage/sites/${siteId}/sessions/${sessionId}/approval`,

  // Ask Helios — advisory SOP Q&A (structured, non-streaming)
  AI_ASK_HELIOS: '/api/v1/ai/ask-helios',
  AI_ASK_HELIOS_STREAM: '/api/v1/ai/ask-helios/stream',

  // EnergyFluo fault + root-cause brief for the alarm detail screen
  AI_ALARM_BRIEF: '/api/v1/ai/alarm-brief',

  // Ask Helios conversation history (resume / replay / rename / archive)
  AI_CONVERSATIONS: '/api/v1/ai/conversations',

  // Object Store endpoints
  OBJECT_STORE: '/api/v1/object-store',
  OBJECT_STORE_OBJECT: (key: string) => `/api/v1/object-store/objects/${key}`,
  OBJECT_STORE_UPLOAD: (key: string) => `/api/v1/object-store/upload/${key}`,
  // A site's own objects; the BFF checks the caller's access to the site.
  OBJECT_STORE_SITE_OBJECT: (siteId: string, key: string) =>
    `/api/v1/object-store/sites/${siteId}/objects/${key}`,
  OBJECT_STORE_SITE_DOWNLOAD: (siteId: string, key: string) =>
    `/api/v1/object-store/sites/${siteId}/download/${key}`,

  // Plan endpoints
  PLANS: '/api/v1/plans',

  // HITL (Human-in-the-Loop) endpoints
  HITL_APPROVALS: '/api/v1/hitl/approvals',
  HITL_REVIEW_DECISIONS: (reviewId: string) => `/api/v1/hitl/reviews/${reviewId}/decisions`,

  // OMS endpoints (Organization Management Service)
  OMS_ORGANIZATIONS: '/api/v1/oms/organizations',
  OMS_ORGANIZATION_BY_ID: (orgId: string) => `/api/v1/oms/organizations/${orgId}`,
  OMS_LEVELS: (orgId: string) => `/api/v1/oms/organizations/${orgId}/levels`,
  OMS_LEVEL_BY_ID: (orgId: string, levelId: string) =>
    `/api/v1/oms/organizations/${orgId}/levels/${levelId}`,
  OMS_REGIONS: (orgId: string) => `/api/v1/oms/organizations/${orgId}/regions`,
  OMS_REGION_BY_ID: (orgId: string, regionId: string) =>
    `/api/v1/oms/organizations/${orgId}/regions/${regionId}`,
  OMS_SITES: (orgId: string) => `/api/v1/oms/organizations/${orgId}/sites`,
  OMS_SITE_BY_ID: (orgId: string, siteId: string) =>
    `/api/v1/oms/organizations/${orgId}/sites/${siteId}`,

  // Provisioning (Qcells-internal). Creates go through these rather than the
  // OMS routes above: each starts a Temporal workflow that also provisions the
  // site repository and waits on IAM reconciliation, which a bare OMS create
  // would skip. See front/src/provisioning/README.md for design/context.
  PROVISIONING_ORGANIZATIONS: '/api/v1/provisioning/organizations',
  PROVISIONING_SITES: '/api/v1/provisioning/sites',

  // Alarm Event Management (AEM) API endpoints — scoped to a site
  ALARMS: (siteId: string) => `/api/v1/sites/${siteId}/alarms`,
  // The operator entry point into the alarm state machine, and the only write
  // AEM exposes on an alarm. Keyed by alarm_id: the same rule fires repeatedly
  // for the same equipment, so alarm_code cannot address one occurrence.
  // (PATCH /alarms/{alarm_code} was removed upstream.)
  ALARM_APPLY_EVENT: (siteId: string, alarmId: string) =>
    `/api/v1/sites/${siteId}/alarms/${encodeURIComponent(alarmId)}/apply-event`,
  // Claim or release an alarm: the body is { action: 'assign_to_me' | 'release' }.
  // The BFF resolves who "me" is, so the client never names an account.
  ALARM_ASSIGNMENT: (siteId: string, alarmId: string) =>
    `/api/v1/sites/${siteId}/alarms/${encodeURIComponent(alarmId)}/assignment`,
  ANOMALIES: (siteId: string) => `/api/v1/sites/${siteId}/anomalies`,
  // Promote is keyed by alarm_id and affects exactly one row. It used to be
  // keyed by alarm_code (the rule), which flipped every matching finding at the
  // site in a single unbounded UPDATE.
  ANOMALY_PROMOTE: (siteId: string, alarmId: string) =>
    `/api/v1/sites/${siteId}/anomalies/${encodeURIComponent(alarmId)}/promote`,

  // Alarm-rule registry (aem.alarm_rule — rule definitions + related documents), scoped to a site
  ALARM_RULE_BY_ID: (siteId: string, ruleId: string) =>
    `/api/v1/sites/${siteId}/alarm-rules/${ruleId}`,

  // SOP Factory Catalog API (read-only SOP/policy-as-code artifacts)
  SOP_FACTORY_ARTIFACTS: '/api/v1/sop-factory/artifacts',
  SOP_FACTORY_ARTIFACT_BY_ID: (id: string) => `/api/v1/sop-factory/artifacts/${id}`,

  // ADM: the same artifacts, scoped to a site (behind adm-sop-catalog-enabled)
  ADM_SITE_ARTIFACTS: (siteId: string) =>
    `/api/v1/adm/sites/${encodeURIComponent(siteId)}/artifacts`,
  ADM_SITE_ARTIFACT_BY_ID: (siteId: string, id: string) =>
    `/api/v1/adm/sites/${encodeURIComponent(siteId)}/artifacts/${encodeURIComponent(id)}`,

  // Telemetry API (observed/forecast equipment timeseries, via the BFF relay)
  TELEMETRY_EQUIPMENT_OBSERVATIONS: (siteId: string, equipmentId: string) =>
    `/api/v1/telemetry/sites/${siteId}/equipment/${equipmentId}/observations`,
  // Logical-node telemetry is aggregated across the node's members by its
  // aggregation profile, so it takes no resolution/rollup — the profile decides.
  TELEMETRY_LOGICAL_NODE_OBSERVATIONS: (siteId: string, logicalNodeId: string) =>
    `/api/v1/telemetry/sites/${siteId}/logical-nodes/${logicalNodeId}/observations`,
  TELEMETRY_LOGICAL_NODE_FORECASTS: (siteId: string, logicalNodeId: string) =>
    `/api/v1/telemetry/sites/${siteId}/logical-nodes/${logicalNodeId}/forecasts`,

  // Live view composed endpoints. Unlike the relays above, one call returns
  // every chiller-plant card's series already aggregated — the fan-out, unit
  // handling and cooling-load maths happen in the BFF.
  LIVE_CHILLER_PLANT: (siteId: string) => `/api/v1/live/sites/${siteId}/chiller-plant`,
  // Total HVAC *electrical* load — chillers, chilled-water pumps and CRAHs
  // summed. The BFF reads one logical node per device group, which is why this
  // is one request here and ~19 upstream. Takes `orgId`: membership is
  // org-scoped and names the nodes.
  LIVE_HVAC_LOAD: (siteId: string) => `/api/v1/live/sites/${siteId}/hvac-load`,

  // Monetization API (logical nodes — the source of the logicalNodeIds the
  // telemetry routes above resolve — plus aggregation profiles and state)
  MONETIZATION_LOGICAL_NODES: (orgId: string, siteId: string) =>
    `/api/v1/monetization/organizations/${orgId}/sites/${siteId}/logical-nodes`,
  MONETIZATION_LOGICAL_NODE: (orgId: string, siteId: string, logicalNodeId: string) =>
    `/api/v1/monetization/organizations/${orgId}/sites/${siteId}/logical-nodes/${logicalNodeId}`,
  MONETIZATION_LOGICAL_NODE_STATE: (orgId: string, siteId: string, logicalNodeId: string) =>
    `/api/v1/monetization/organizations/${orgId}/sites/${siteId}/logical-nodes/${logicalNodeId}/state`,

  // Weather API (coordinate-based forecasts/observations, relayed by the BFF)
  WEATHER_FORECASTS: '/api/v1/weather/forecasts',
  WEATHER_OBSERVATIONS: '/api/v1/weather/observations',

  // Work orders (BFF proxy for the helios-work-order work-order-api, itself a proxy over an
  // external WOMS). Summary is a separate call because the WOMS reports no
  // totals, so the counts have to be paged for.
  // Work orders belong to a site. A null site is the whole fleet, which only
  // the deprecated unscoped routes can list.
  WORK_ORDERS: workOrdersBase,
  WORK_ORDER_BY_ID: (siteId: string | null, workOrderId: string) =>
    `${workOrdersBase(siteId)}/${workOrderId}`,
  WORK_ORDERS_SUMMARY: (siteId: string | null) => `${workOrdersBase(siteId)}/summary`,
  // Directories a work order's provider / business_owner must come from.
  WORK_ORDER_BUSINESS_OWNERS: '/api/v1/work-orders/business-owners',
  WORK_ORDER_PROVIDERS: '/api/v1/work-orders/providers',
};

// Coordinates the header weather widget requests forecasts for. Single-site
// deployment today: Prime Edge GA-10, Marietta, GA. When the app becomes
// multi-site this should come from the active site's metadata instead.
export const WEATHER_LOCATION = {
  latitude: 33.9261,
  longitude: -84.4859,
};

// Qcells Design System Colors
export const COLORS = {
  primary: '#00C6C1',
  secondary: '#0095D6',
  background: '#0a0e1a',
  cardBackground: '#fafafa',
  text: {
    primary: '#ffffff',
    secondary: '#a0a0a0',
    muted: '#666666',
  },
  status: {
    success: '#00C6C1',
    warning: '#FFA500',
    error: '#FF4444',
    info: '#0095D6',
  },
  border: '#2a2f3e',
};

// Pagination
export const PAGINATION = {
  DEFAULT_PAGE_SIZE: 20,
  PAGE_SIZE_OPTIONS: [10, 20, 50, 100],
};

// Local Storage Keys
// JWT tokens are managed by MSAL.js in its own localStorage keys.
// These keys are for app-level data only.
export const STORAGE_KEYS = {
  USER_DATA: 'helios_user_data',
  USER_PREFERENCES: 'helios_user_prefs',
};
